import React, { useState, useCallback, useEffect } from 'react';

import { TitleBlocContainer, MentionContainer } from 'modules/shared-cms-advanced';
import { CtaButton } from 'modules/shared-ui/components';
import { NumberedBullets } from 'modules/shared-ui-advanced';
import {
    AppointmentForm,
    Appointment,
    PersonalInfoForm,
    PersonalInfo,
    GroupCheckboxesOption,
} from 'modules/mariage/components';
import {
    UpdateAppointmentInput,
    CreateAppointmentInput,
    GetAvailabilitiesInput,
    Availability,
    Answer,
    AppointmentStatus,
} from 'modules/mariage/models';
import { formatTechDate } from 'modules/shared/utils';
import { AppointmentCreationProps } from './appointment-creation-type';

import enhancer from './appointment-creation-enhancer';

import styles from './appointment-creation-page.module.css';

interface Summary {
    reference: string;
    fullName: string;
}

const innerPage: React.FC<AppointmentCreationProps> = React.memo(
    ({
        titleBlocPicture,
        sessionData,
        getAvailabilities,
        updateSessionData,
        createAppointment,
        updateAppointment,
        deleteAppointment,
        getCustomer,
        appointmentTypes,
        ...rest
    }: AppointmentCreationProps) => {
        // On initialise la session si elle est vide
        if (!sessionData) {
            updateSessionData({
                appointmentModification: false,
            });

            return <React.Fragment />;
        }

        const { appointmentModification, customerId, appointmentRef } = sessionData;
        const [activeTab, setActiveTab] = useState(0);
        const [appointment, setAppointment] = useState(new Appointment());
        const [personalInfo, setPersonalInfo] = useState(new PersonalInfo());
        const [summary, setSummary] = useState<Summary>({ reference: '', fullName: '' });

        const isString = (obj: string | GroupCheckboxesOption[]): obj is string => {
            return typeof obj === 'string';
        };

        useEffect(() => {
            const titleElement = document.getElementById('rdv-bullets');
            if (titleElement) {
                titleElement.scrollIntoView({ behavior: 'smooth' });
            }
        }, [activeTab]);

        const mapAnswers = useCallback(
            (rawAnswers: { questionId: number; answer: string | GroupCheckboxesOption[] }[]): Answer[] => {
                return rawAnswers
                    .filter(ans => ans && ans !== null)
                    .map(ans => {
                        return {
                            questionId: ans.questionId,
                            values: !isString(ans.answer)
                                ? ans.answer.filter(ans2 => ans2.checked).map(ans2 => ans2.label)
                                : [ans.answer],
                        };
                    });
            },
            [],
        );

        const handleAvailabilitiesSearch = useCallback(
            (typeId: number, date: Date): Promise<Availability[]> => {
                const input: GetAvailabilitiesInput = {
                    typeId,
                    date: formatTechDate(date),
                };

                return getAvailabilities(input);
            },
            [getAvailabilities],
        );

        const handleSummary = useCallback(({ reference, userId: id }: AppointmentStatus) => {
            setSummary({ ...summary, reference });

            getCustomer({ id }).then(customer => {
                const fullName = `${customer.firstName} ${customer.lastName}`;
                setSummary({ reference, fullName });
            });
        }, []);

        const handleAppointmentUpdate = useCallback(
            (appt: Appointment) => {
                setAppointment(appt);

                if (!customerId) {
                    setActiveTab(1);
                } else {
                    if (appointmentModification && appointmentRef) {
                        deleteAppointment({ id: appointmentRef });
                    }

                    const { date, answers, ...apptRest } = appt;
                    const mappedAnswers = mapAnswers(answers);

                    if (date) {
                        const input: UpdateAppointmentInput = {
                            customerId,
                            date: formatTechDate(date),
                            answers: mappedAnswers,
                            ...apptRest,
                        };
                        updateAppointment(input).then(status => {
                            updateSessionData({
                                appointmentModification: false,
                                appointmentRef: undefined,
                                customerId: undefined,
                            });

                            handleSummary(status);
                            setActiveTab(2);
                        });
                    }
                }
            },
            [
                updateAppointment,
                deleteAppointment,
                updateSessionData,
                mapAnswers,
                appointmentModification,
                appointmentRef,
                customerId,
            ],
        );

        const handleAppointmentCreate = useCallback(
            (info: PersonalInfo) => {
                setPersonalInfo(info);

                const { date, weddingDate, answers, ...apptRest } = appointment;
                const { birthDate, newsletter, openWeddingList, contactIfSlotAvailable, ...infoRest } = info;
                const mappedAnswers = mapAnswers(answers);

                if (date) {
                    const input: CreateAppointmentInput = {
                        birthDate: formatTechDate(birthDate || ''),
                        newsletter: Boolean(newsletter),
                        openWeddingList: Boolean(openWeddingList),
                        contactIfSlotAvailable: Boolean(contactIfSlotAvailable),
                        ...infoRest,
                        date: formatTechDate(date),
                        weddingDate: formatTechDate(weddingDate || ''),
                        answers: mappedAnswers,
                        ...apptRest,
                    };
                    createAppointment(input).then(status => {
                        handleSummary(status);
                        setActiveTab(2);
                    });
                }
            },
            [appointment, createAppointment, mapAnswers],
        );

        const handleCancel = useCallback(() => {
            setActiveTab(0);
        }, []);

        const tabs = [
            {
                title: '1. LE RENDEZ-VOUS',
                component: (
                    <React.Fragment>
                        <AppointmentForm
                            model={appointment}
                            appointmentTypes={appointmentTypes}
                            onAvailabilitiesSearch={handleAvailabilitiesSearch}
                            onSuccess={handleAppointmentUpdate}
                        />
                    </React.Fragment>
                ),
            },
            {
                title: '2. INFORMATIONS PERSONNELLES',
                component: (
                    <React.Fragment>
                        <PersonalInfoForm
                            model={personalInfo}
                            onSuccess={handleAppointmentCreate}
                            onCancel={handleCancel}
                        />
                    </React.Fragment>
                ),
            },
            {
                title: '',
                component: (
                    <React.Fragment>
                        <div className={styles.messageContainer}>
                            <div className={styles.title}>MERCI</div>
                            <div>POUR VOTRE INSCRIPTION</div>

                            <div className={styles.summary}>
                                <p>Votre rendez-vous est bien enregistré.</p>
                                <p>
                                    Un e-mail et/ou un SMS (en fonction des informations communiquées) de notification
                                    vous a été envoyé avec la réfrence du rendez-vous.
                                </p>
                                <p>
                                    Votre référence rendez-vous est : <b>{summary.reference}</b>
                                </p>
                                {summary.fullName !== '' && (
                                    <p>
                                        Nom : <b>{summary.fullName}</b>
                                    </p>
                                )}
                            </div>

                            <div className={styles.buttonsContainer}>
                                <div style={{ marginRight: '20px' }}>
                                    <CtaButton ctaText="TOUTES LES ROBES" ctaLink="/mariage/la-robe/modeles" />
                                </div>
                                <div>
                                    <CtaButton
                                        ctaText="LA LISTE DE MARIAGE"
                                        ctaLink="/listes/creer-liste/type/mariage"
                                    />
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                ),
            },
        ];

        return (
            <React.Fragment>
                <div className={styles.rootContainer}>
                    {titleBlocPicture && <TitleBlocContainer titleBlocPicture={titleBlocPicture} {...rest} />}
                    {activeTab !== tabs.length - 1 && (
                        <div id="rdv-bullets" className={styles.bullets}>
                            <NumberedBullets initialValue={activeTab + 1} maxValue={tabs.length - 1} />
                        </div>
                    )}

                    <div className={styles.tabContainer}>
                        {tabs.map((item, i) => {
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={i}>
                                    {activeTab === i && <div className={styles.tabTitle}>{item.title}</div>}
                                </React.Fragment>
                            );
                        })}

                        {tabs.map((item, i) => {
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={i}>
                                    <div
                                        className={`${styles.tabContent} ${activeTab !== i ? `${styles.hidden}` : ''}`}
                                    >
                                        {item.component}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <div className={styles.gdprBloc}>
                        <MentionContainer {...rest} />
                    </div>
                </div>
            </React.Fragment>
        );
    },
);

export const AppointmentCreationPage = enhancer(innerPage);
